const FALLBACK = '/'

// Transient auth pages: there is nothing meaningful to return to on the
// OIDC callback page or the legacy /login redirect, so both collapse to
// the root in both directions (capture and sanitize).
const TRANSIENT_PATHS = new Set(['/callback', '/login'])

/**
 * Capture the current URL to return to after a sign-in round-trip.
 *
 * Returns `pathname + search` so a deep link's query state (tabs, filters)
 * survives login. The URL fragment is intentionally omitted — we preserve
 * only `pathname + search` rather than rely on the fragment surviving the
 * sign-in redirect round-trip.
 *
 * `AuthGate` / `PublicShell` render the sign-in screen in place without
 * navigating, so at call time `location` already is the intended
 * destination — one function serves every call site.
 */
export function captureReturnTo(): string {
  const { pathname, search } = globalThis.location
  if (TRANSIENT_PATHS.has(pathname)) return FALLBACK
  return `${pathname}${search}`
}

/**
 * Validate a return-to value read back from OIDC `state`.
 *
 * Accepts only a same-origin absolute path: a string starting with a
 * single `/`, not protocol-relative (`//…`) or backslash-tricked (`/\…`),
 * and whose path portion is not a transient auth page. Anything else falls
 * back to the root — this closes the open-redirect vector even though
 * `state` is normally same-origin and tamper-resistant.
 */
export function sanitizeReturnTo(value: unknown): string {
  if (typeof value !== 'string') return FALLBACK
  if (!value.startsWith('/')) return FALLBACK
  if (value.startsWith('//') || value.startsWith('/\\')) return FALLBACK
  // URL parsing strips tab/CR/LF, which would turn `/\t/evil.com` into `//evil.com`.
  if (/\p{Cc}/u.test(value)) return FALLBACK
  const path = value.split('?')[0].split('#')[0]
  if (TRANSIENT_PATHS.has(path)) return FALLBACK
  return value
}
